/**
 * React Query cache keys.
 *
 * **Every key that returns bilingual content includes the language.** The server
 * resolves display fields per request, so a key without the language would serve
 * cached English titles after the user switches to Arabic -- a bug that is
 * invisible unless you actually test the toggle.
 */

import type { BookFilters, Language } from '@/types/api'

export const queryKeys = {
  libraryInfo: (language: Language) => ['libraryInfo', language] as const,
  catalogStats: (language: Language) => ['catalogStats', language] as const,

  categoryTree: (language: Language) => ['categoryTree', language] as const,
  category: (slug: string, language: Language) => ['category', slug, language] as const,

  books: (filters: BookFilters, language: Language) => ['books', language, filters] as const,
  book: (slug: string, language: Language) => ['book', slug, language] as const,
  recentBooks: (language: Language) => ['recentBooks', language] as const,
  suggestions: (term: string, language: Language) => ['suggestions', language, term] as const,

  authors: (language: Language) => ['authors', language] as const,

  reservations: (filters: Record<string, unknown>, language: Language) =>
    ['reservations', language, filters] as const,
  reservation: (id: number, language: Language) => ['reservation', id, language] as const,
  dashboard: (language: Language) => ['dashboard', language] as const,
  pickupSlots: (days: number, language: Language) => ['pickupSlots', language, days] as const,

  // Names and addresses only: nothing here is bilingual.
  members: (filters: Record<string, unknown>) => ['members', filters] as const,

  me: () => ['me'] as const,
} as const

/** Prefixes used to invalidate whole families after a mutation. */
export const queryScopes = {
  books: ['books'] as const,
  book: ['book'] as const,
  reservations: ['reservations'] as const,
  reservation: ['reservation'] as const,
  dashboard: ['dashboard'] as const,
  categoryTree: ['categoryTree'] as const,
  pickupSlots: ['pickupSlots'] as const,
  libraryInfo: ['libraryInfo'] as const,
  members: ['members'] as const,
  me: ['me'] as const,
} as const
